import { Badge, Button, Dialog, Field, Input, Portal, SimpleGrid, Stack } from '@chakra-ui/react'
import { LuX } from 'react-icons/lu'
import { AxiosError } from 'axios'
import { useParams } from 'react-router'
import CustomDialogBackdrop from '@/components/CustomDialogBackdrop'
import { useCheckoutSubscription } from '@/hooks/subscriptions/useCheckoutSubscription'
import type { ApiErrorResponse } from '@/lib/api'
import PhoneInput from '@/components/PhoneInput'
import CepInput from '@/components/CepInput'
import CpfCnpjInput from '@/components/CpfCnpjInput'
import BrazilianStateSelect from '@/components/BrazilianStateSelect'

type Props = {
  open: boolean
  onOpenChange: (open: boolean) => void
  planId: number
  planName: string
}

const CheckoutModal = ({ open, onOpenChange, planId, planName }: Props) => {
  const { agendaId } = useParams<{ agendaId: string }>()
  const checkoutMutation = useCheckoutSubscription(agendaId!)

  const validationErrors = (checkoutMutation.error as AxiosError<ApiErrorResponse> | null)
    ?.response?.data?.validation_errors

  const handleOpenChange = (nextOpen: boolean) => {
    if (!nextOpen) checkoutMutation.reset()
    onOpenChange(nextOpen)
  }

  const handleSubmit = (e: { preventDefault(): void; currentTarget: HTMLFormElement }) => {
    e.preventDefault()
    const data = new FormData(e.currentTarget)

    checkoutMutation.mutate(
      {
        plan_id: planId,
        customer_name: (data.get('customer_name') as string).trim(),
        customer_email: (data.get('customer_email') as string).trim(),
        customer_phone: (data.get('customer_phone') as string).trim(),
        customer_tax_id: (data.get('customer_tax_id') as string).trim(),
        customer_postal_code: (data.get('customer_postal_code') as string).trim(),
        customer_address: (data.get('customer_address') as string).trim(),
        customer_address_number: (data.get('customer_address_number') as string).trim(),
        customer_complement: (data.get('customer_complement') as string).trim(),
        customer_city: (data.get('customer_city') as string).trim(),
        customer_province: (data.get('customer_province') as string).trim(),
      },
      {
        onSuccess: ({ checkout_url }) => {
          handleOpenChange(false)
          window.open(checkout_url, '_blank')
        },
      },
    )
  }

  return (
    <Dialog.Root
      size="lg"
      placement="center"
      open={open}
      onOpenChange={({ open }) => handleOpenChange(open)}
    >
      <Portal>
        <CustomDialogBackdrop />
        <Dialog.Positioner>
          <Dialog.Content m={6}>
            <form onSubmit={handleSubmit}>
              <Dialog.Header>
                <Dialog.Title>
                  Assinar plano{' '}
                  <Badge colorPalette="primary" variant="solid" size="sm" ms="1">
                    {planName}
                  </Badge>
                </Dialog.Title>
                <Dialog.CloseTrigger asChild>
                  <Button variant="ghost" size="sm" aria-label="Fechar">
                    <LuX />
                  </Button>
                </Dialog.CloseTrigger>
              </Dialog.Header>

              <Dialog.Body>
                <Stack gap="4">
                  <SimpleGrid columns={{ base: 1, sm: 2 }} gap="4">
                    <Field.Root required invalid={!!validationErrors?.customer_name}>
                      <Field.Label>
                        Nome completo
                        <Field.RequiredIndicator />
                      </Field.Label>
                      <Input name="customer_name" placeholder="João da Silva" variant="subtle" />
                      {validationErrors?.customer_name?.map((error, i) => (
                        <Field.ErrorText key={i}>{error}</Field.ErrorText>
                      ))}
                    </Field.Root>

                    <Field.Root required invalid={!!validationErrors?.customer_tax_id}>
                      <Field.Label>
                        CPF / CNPJ
                        <Field.RequiredIndicator />
                      </Field.Label>
                      <CpfCnpjInput name="customer_tax_id" variant="subtle" />
                      {validationErrors?.customer_tax_id?.map((error, i) => (
                        <Field.ErrorText key={i}>{error}</Field.ErrorText>
                      ))}
                    </Field.Root>

                    <Field.Root required invalid={!!validationErrors?.customer_email}>
                      <Field.Label>
                        E-mail
                        <Field.RequiredIndicator />
                      </Field.Label>
                      <Input
                        name="customer_email"
                        type="email"
                        placeholder="email@exemplo.com"
                        variant="subtle"
                      />
                      {validationErrors?.customer_email?.map((error, i) => (
                        <Field.ErrorText key={i}>{error}</Field.ErrorText>
                      ))}
                    </Field.Root>

                    <Field.Root required invalid={!!validationErrors?.customer_phone}>
                      <Field.Label>
                        Telefone
                        <Field.RequiredIndicator />
                      </Field.Label>
                      <PhoneInput name="customer_phone" placeholder="(11) 90000-0000" variant="subtle" />
                      {validationErrors?.customer_phone?.map((error, i) => (
                        <Field.ErrorText key={i}>{error}</Field.ErrorText>
                      ))}
                    </Field.Root>

                    <Field.Root required invalid={!!validationErrors?.customer_postal_code}>
                      <Field.Label>
                        CEP
                        <Field.RequiredIndicator />
                      </Field.Label>
                      <CepInput name="customer_postal_code" placeholder="00000-000" variant="subtle" />
                      {validationErrors?.customer_postal_code?.map((error, i) => (
                        <Field.ErrorText key={i}>{error}</Field.ErrorText>
                      ))}
                    </Field.Root>

                    <Field.Root required invalid={!!validationErrors?.customer_city}>
                      <Field.Label>
                        Cidade
                        <Field.RequiredIndicator />
                      </Field.Label>
                      <Input name="customer_city" placeholder="São Paulo" variant="subtle" />
                      {validationErrors?.customer_city?.map((error, i) => (
                        <Field.ErrorText key={i}>{error}</Field.ErrorText>
                      ))}
                    </Field.Root>

                    <Field.Root required invalid={!!validationErrors?.customer_province}>
                      <Field.Label>
                        Estado (UF)
                        <Field.RequiredIndicator />
                      </Field.Label>
                      <BrazilianStateSelect name="customer_province" variant="subtle" />
                      {validationErrors?.customer_province?.map((error, i) => (
                        <Field.ErrorText key={i}>{error}</Field.ErrorText>
                      ))}
                    </Field.Root>

                    <Field.Root required invalid={!!validationErrors?.customer_address}>
                      <Field.Label>
                        Endereço
                        <Field.RequiredIndicator />
                      </Field.Label>
                      <Input
                        name="customer_address"
                        placeholder="Rua das Flores"
                        variant="subtle"
                      />
                      {validationErrors?.customer_address?.map((error, i) => (
                        <Field.ErrorText key={i}>{error}</Field.ErrorText>
                      ))}
                    </Field.Root>

                    <Field.Root required invalid={!!validationErrors?.customer_address_number}>
                      <Field.Label>
                        Número
                        <Field.RequiredIndicator />
                      </Field.Label>
                      <Input name="customer_address_number" placeholder="123" variant="subtle" />
                      {validationErrors?.customer_address_number?.map((error, i) => (
                        <Field.ErrorText key={i}>{error}</Field.ErrorText>
                      ))}
                    </Field.Root>

                    <Field.Root invalid={!!validationErrors?.customer_complement}>
                      <Field.Label>Complemento</Field.Label>
                      <Input name="customer_complement" placeholder="Apto 42" variant="subtle" />
                      {validationErrors?.customer_complement?.map((error, i) => (
                        <Field.ErrorText key={i}>{error}</Field.ErrorText>
                      ))}
                    </Field.Root>
                  </SimpleGrid>
                </Stack>
              </Dialog.Body>

              <Dialog.Footer>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => handleOpenChange(false)}
                  disabled={checkoutMutation.isPending}
                >
                  Cancelar
                </Button>
                <Button
                  size="sm"
                  type="submit"
                  colorPalette="primary"
                  loading={checkoutMutation.isPending}
                >
                  Assinar
                </Button>
              </Dialog.Footer>
            </form>
          </Dialog.Content>
        </Dialog.Positioner>
      </Portal>
    </Dialog.Root>
  )
}

export default CheckoutModal
